import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const TOKEN_KEY = "deck_token";
export const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const LoginPage = ({ onSuccess }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/login`, { password: pw.trim() }, { withCredentials: true });
      if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="login-page" className="relative min-h-screen bg-ink-deep text-white overflow-hidden">
      <img src="/img/rendering.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F2235] via-[#0F2235]/90 to-[#0F2235]/40" />
      <div className="relative min-h-screen flex flex-col justify-between px-8 sm:px-16 py-12 max-w-[1440px] mx-auto">
        <div className="font-mono text-[11px] tracking-[0.42em] text-arq-mid">ARQITEK</div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-xl">
          <div className="font-mono text-[11px] tracking-[0.22em] text-white/55 uppercase">Joint Venture Partner Presentation · Confidential</div>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl leading-[1.02]">Murrieta Family Commons Project</h1>
          <p className="mt-5 text-white/70">This presentation is private. Enter the deck password provided by Arqitek to continue.</p>
          <form onSubmit={submit} className="mt-10">
            <label className="text-xs text-white/60" htmlFor="deck-password">Deck password</label>
            <div className="mt-2 flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-xl pl-5 pr-1.5 py-1.5 focus-within:border-arq-mid transition-colors">
              <Lock size={16} className="text-white/50" />
              <input
                id="deck-password"
                data-testid="login-password-input"
                type={show ? "text" : "password"}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="current-password"
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="flex-1 bg-transparent outline-none text-white placeholder:text-white/35 py-2"
                placeholder="Enter password"
              />
              <button
                type="button"
                data-testid="login-toggle-password-btn"
                onClick={() => setShow(!show)}
                aria-label={show ? "Hide password" : "Show password"}
                title={show ? "Hide password" : "Show password"}
                className="h-9 w-9 grid place-items-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                {show ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
              <button data-testid="login-submit-btn" type="submit" disabled={busy || !pw} className="flex items-center gap-2 rounded-full bg-arq px-5 py-2.5 text-sm hover:bg-arq-dark transition-colors disabled:opacity-50">
                {busy ? "Checking…" : "Enter"} <ArrowRight size={15} />
              </button>
            </div>
            {err && <div data-testid="login-error" className="mt-3 text-sm text-[#F2B8BF]">{err}</div>}
          </form>
        </motion.div>
        <div className="text-[11px] font-mono tracking-[0.16em] text-white/35">PRELIMINARY · CONFIDENTIAL · OCT 2026</div>
      </div>
    </div>
  );
};

export const LoginGate = ({ children }) => {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/me`, { withCredentials: true, headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, []);
  if (authed === null) return <div data-testid="auth-loading" className="min-h-screen bg-ink-deep" />;
  if (!authed) return <LoginPage onSuccess={() => setAuthed(true)} />;
  return children;
};
